// AnlageSpeichernLeiste.tsx — rechte Seite der Fußleiste in der Kontaktanlage
// (Firma und Privat), User 27.09.2026:
//
//   ⚠ (Hover: was fehlt)   [Analysemodus starten]   [Speichern]
//
// Statt des langen roten Satzes „Bitte Pflichtfelder (*) ausfüllen" steht nur
// ein Warndreieck; der Text kommt beim Überfahren. Speichern und Analysemodus
// sind nicht mehr gesperrt: Ein Klick bei offenen Punkten öffnet ein Popup, das
// sagt, WELCHE Felder fehlen — ein ausgegrauter Knopf erklärt das nicht.
// „Analysemodus starten" gibt es nur bei einer Firma (der Aufrufer entscheidet);
// die Privatanlage zeigt ihn ausgegraut mit Begründung (`analysemodusGesperrt`,
// User 28.09.2026), damit niemand ihn dort sucht.

import * as React from "react";
import { Tooltip, makeStyles, shorthands } from "@fluentui/react-components";
import { REFINED } from "../risikoanalyseStep/tokens";

/** Ein offener Punkt, der das Speichern verhindert. */
export type AnlageMangel = {
  /** Kurztext für Tooltip und Popup, z. B. „Bitte Pflichtfelder (*) ausfüllen". */
  hinweis: string;
  /** Die betroffenen Felder, z. B. ["Firmenname", "Rechtsform"]. */
  felder?: string[];
  /**
   * `false`: nur Hinweis (Dreieck), Speichern bleibt möglich — etwa für
   * Formatfehler aus dem Bestand im Bearbeiten-Modus der Akte.
   */
  sperrt?: boolean;
};

const useStyles = makeStyles({
  rechts: { display: "flex", alignItems: "center", columnGap: "10px" },
  warn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "30px",
    height: "30px",
    ...shorthands.borderRadius("8px"),
    color: REFINED.err,
    backgroundColor: REFINED.errBg,
    cursor: "help",
  },
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 100002,
    backgroundColor: "rgba(20,8,20,0.34)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.padding("24px"),
  },
  dialog: {
    width: "min(460px, 100%)",
    backgroundColor: REFINED.white,
    ...shorthands.borderRadius("14px"),
    boxShadow: "0 18px 50px rgba(40,10,40,0.25)",
    ...shorthands.overflow("hidden"),
    fontFamily: REFINED.fontUI,
  },
  kopf: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    ...shorthands.padding("15px", "18px"),
    ...shorthands.borderBottom("2px", "solid", REFINED.goldLine),
  },
  titel: { ...shorthands.margin("0"), fontSize: "1.05rem", fontWeight: 800, color: REFINED.ink },
  inhalt: { ...shorthands.padding("16px", "18px"), fontSize: "0.9rem", color: REFINED.ink2, lineHeight: 1.5 },
  fuss: {
    display: "flex",
    justifyContent: "flex-end",
    ...shorthands.padding("12px", "18px"),
    ...shorthands.borderTop("1px", "solid", REFINED.line),
    backgroundColor: REFINED.fieldBg,
  },
  ok: {
    ...shorthands.border("0"),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.aub,
    color: REFINED.white,
    fontFamily: REFINED.fontUI,
    fontWeight: 700,
    fontSize: "0.88rem",
    ...shorthands.padding("8px", "18px"),
    cursor: "pointer",
  },
  liste: { ...shorthands.margin("8px", "0", "0"), paddingLeft: "20px", lineHeight: 1.6 },
  gruppe: { ...shorthands.margin("10px", "0", "0"), fontWeight: 600 },
  analyse: {
    ...shorthands.border("1.5px", "solid", REFINED.aub),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.9rem",
    ...shorthands.padding("9px", "16px"),
    cursor: "pointer",
    ":hover": { backgroundColor: REFINED.aub50 },
    ":disabled": { opacity: 0.55, cursor: "default" },
  },
});

const WarnDreieck = (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M10 2.8 18 16.5H2L10 2.8Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path d="M10 8v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="10" cy="14.4" r="1" fill="currentColor" />
  </svg>
);

export function AnlageSpeichernLeiste({
  maengel,
  isSubmitting,
  onSpeichern,
  onAnalysemodus,
  analysemodusGesperrt,
  saveClassName,
  nurLesen,
  speichernText = "Speichern",
}: {
  maengel: AnlageMangel[];
  isSubmitting: boolean;
  onSpeichern: () => void;
  /** Nur bei einer Firma — ohne ihn gibt es den Knopf nicht. */
  onAnalysemodus?: () => void;
  /**
   * Knopf ausgegraut zeigen, mit diesem Text als Tooltip (z. B. „Nur für
   * Firmen"). Gewinnt gegen `onAnalysemodus`.
   */
  analysemodusGesperrt?: string;
  /** Klasse des Speichern-Knopfs der jeweiligen Maske (gleiche Optik wie bisher). */
  saveClassName: string;
  /** Ohne Schreibrecht: kein Speichern-Knopf. */
  nurLesen?: boolean;
  /**
   * Beschriftung des Speichern-Knopfs. Aus einer Schnellanlage geöffnet
   * „Übernehmen" bzw. „Weiter" (Mail zuweisen) — er speichert und trägt den
   * Kontakt dort ein, wo die Schnellanlage stand (User 02.10.2026).
   */
  speichernText?: string;
}) {
  const s = useStyles();
  const [popup, setPopup] = React.useState<null | "speichern" | "analyse">(null);
  const offen = maengel.length > 0;
  const sperrend = maengel.filter((m) => m.sperrt !== false);
  const tooltip = maengel.map((m) => m.hinweis).join(" · ");

  const ausloesen = (was: "speichern" | "analyse") => {
    if (isSubmitting) return;
    if (sperrend.length > 0) {
      setPopup(was);
      return;
    }
    if (was === "analyse") onAnalysemodus?.();
    else onSpeichern();
  };

  return (
    <div className={s.rechts}>
      {offen && (
        <Tooltip content={tooltip} relationship="label" withArrow>
          <span className={s.warn} role="img" tabIndex={0} data-testid="anlage-warnung">
            {WarnDreieck}
          </span>
        </Tooltip>
      )}
      {analysemodusGesperrt ? (
        <Tooltip content={analysemodusGesperrt} relationship="description" withArrow>
          {/* Ein disabled-Knopf meldet kein Hover — der Rahmen trägt den Tooltip. */}
          <span tabIndex={0} style={{ display: "inline-flex" }} data-testid="analysemodus-gesperrt">
            <button type="button" className={s.analyse} disabled title={analysemodusGesperrt}>
              Analysemodus starten
            </button>
          </span>
        </Tooltip>
      ) : onAnalysemodus && (
        <button
          type="button"
          className={s.analyse}
          disabled={isSubmitting}
          onClick={() => ausloesen("analyse")}
        >
          Analysemodus starten
        </button>
      )}
      {!nurLesen && (
      <button
        type="button"
        className={saveClassName}
        disabled={isSubmitting}
        onClick={() => ausloesen("speichern")}
      >
        {isSubmitting ? "Speichern…" : speichernText}
      </button>
      )}
      {popup !== null && (
        <div className={s.overlay} onMouseDown={(e) => e.target === e.currentTarget && setPopup(null)}>
          <div className={s.dialog} role="alertdialog" aria-modal="true" aria-labelledby="anlage-maengel-titel">
            <div className={s.kopf}>
              <span className={s.warn}>{WarnDreieck}</span>
              <h3 className={s.titel} id="anlage-maengel-titel">
                {popup === "analyse" ? "Analysemodus noch nicht möglich" : "Speichern noch nicht möglich"}
              </h3>
            </div>
            <div className={s.inhalt}>
              {popup === "analyse"
                ? "Für den Analysemodus wird der Kontakt zuerst gespeichert. Dafür fehlt noch:"
                : "Der Kontakt kann noch nicht gespeichert werden. Es fehlt noch:"}
              {sperrend.map((m) => (
                <React.Fragment key={m.hinweis}>
                  <div className={s.gruppe}>{m.hinweis}</div>
                  {m.felder && m.felder.length > 0 && (
                    <ul className={s.liste}>
                      {m.felder.map((f) => (
                        <li key={f}>{f}</li>
                      ))}
                    </ul>
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className={s.fuss}>
              <button type="button" className={s.ok} autoFocus onClick={() => setPopup(null)}>
                Zurück zur Maske
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
